import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ITableHeaderCellProps } from './Interfaces';
import { useTranslations } from '../../contexts/translationContext';
import { useAuth } from '../../contexts/authContext';
import Filter from './Filter';

const TableHeaderCell: React.FC<ITableHeaderCellProps> = ({ tableName, colInfo, colUserSettings, colIndex, colResize, colResizeActiveIndex, onHoverGetIndex, onChangeSort, onChangeFilter, hideShowFilter, onCheckboxButtonClicked }) => {
    const [title, setTitle] = useState<string | undefined>(colInfo.title);
    const { translate } = useTranslations();
    const { isAuthorizedFor } = useAuth();
    const [showFilterMenu, setShowFilterMenu] = useState(false);
    const [resizeHandleHeight, setResizeHandleHeight] = useState(45);

    colInfo.ref = useRef();

    useEffect(() => {
        if (colInfo.title)
            translate(colInfo.title + '.title', tableName, colInfo.title).then(translated => setTitle(translated));
    }, [colInfo.title]);

    if (colUserSettings.visible === false) {
        return null;
    }


    let visible = colInfo.visible;
    if (visible && colInfo.ApplicationFunctions) {
        visible = false;
        colInfo.ApplicationFunctions.forEach((f) => {
            if (isAuthorizedFor(f))
                visible = true;
        });
    }

    if (visible === false) {
        return null;
    }

    function sortClick(e) {
        onChangeSort(colIndex);
    }

    function onHoverSetResizeHandleHeight() {
        let x = document.getElementById(tableName) as HTMLElement;
        setResizeHandleHeight(x.offsetHeight);
    }

    let sortIcon = "sortIcon fa fa-sort";

    if (colUserSettings.sort === "asc") {
        switch (colInfo.dataType) {
            case 'date':
            case 'datetime':
            case 'bool': sortIcon = "sortIcon active fa fa-sort-amount-asc"; break;

            case 'project':
            case 'money':
            case 'decimal':
            case 'percentage':
            case 'number': sortIcon = "sortIcon active fa fa-sort-numeric-asc"; break;

            default:
                sortIcon = "sortIcon active fa fa-sort-alpha-asc";
                break;
        }
    }

    if (colUserSettings.sort === "desc") {
        switch (colInfo.dataType) {
            case 'date':
            case 'datetime':
            case 'bool': sortIcon = "sortIcon active fa fa-sort-amount-desc"; break;

            case 'project':
            case 'money':
            case 'decimal':
            case 'percentage':
            case 'number': sortIcon = "sortIcon active fa fa-sort-numeric-desc"; break;

            default:
                sortIcon = "sortIcon active fa fa-sort-alpha-desc";
                break;
        }
    }

    var style: CSSProperties = {};

    let sort = colUserSettings.sort !== false;
    if (colInfo.dataType === 'image') {
        sort = false;
        if (colUserSettings.width !== 'auto')
            style.width = 34;
    }

    if (colInfo.dataType === 'date') {
        if (colUserSettings.width !== 'auto')
            style.width = 110;
    }

    if (colInfo.dataType === 'datetime') {
        if (colUserSettings.width !== 'auto')
            style.width = 150;
    }

    if (colInfo.dataType === 'user') {
        if (colUserSettings.width !== 'auto')
            style.width = 250;
    }

    if (colInfo.dataType === 'item') {
        if (colUserSettings.width !== 'auto')
            style.width = 200;
    }

    if (colInfo.dataType === 'itemrevision') {
        if (colUserSettings.width !== 'auto')
            style.width = 220;
    }

    if (colUserSettings.width)
        if (colUserSettings.width !== 'auto')
            style.width = colUserSettings.width;

    if (colInfo.maxWidth)
        style.maxWidth = colInfo.maxWidth;

    let minCellWidth = 5
    if (colInfo.sort !== false || colInfo.filterInfo)
        minCellWidth = 25
    if (colInfo.minWidth)
        minCellWidth = colInfo.minWidth

    if (colUserSettings.width && colUserSettings.width !== 'auto' && colUserSettings.width >= minCellWidth)
        style.minWidth = colUserSettings.width
    else if (colInfo.minWidth)
        style.minWidth = colInfo.minWidth

    let classnames = ''
    if (colInfo.align)
        classnames += ' text-' + colInfo.align;

    let hasFilter = false;
    if (colInfo.filterInfo)
        hasFilter = true

    let isButtonColumn = false;
    if (colInfo.headerButtons || colInfo.rowButtons || colInfo.checkboxButton)
        isButtonColumn = true;

    let buttons;
    if (colInfo.headerButtons) {
        buttons = colInfo.headerButtons.map((buttonInfo, index) => {
            if (buttonInfo.applicationFunctions) {
                let btnVisible = false;
                buttonInfo.applicationFunctions.forEach((f) => {
                    if (isAuthorizedFor(f))
                        btnVisible = true;
                })
                if (btnVisible === false)
                    return <></>
            }
            return (
                <OverlayTrigger placement="top" overlay={(<Tooltip id="tooltip">{buttonInfo.text}</Tooltip>)} key={index}>
                    <span style={{ color: buttonInfo.color }} onClick={buttonInfo.onClick}><i className={'fa ' + buttonInfo.icon} /></span>
                </OverlayTrigger>
            )
        });
    }

    if (colInfo.checkboxButton) {
        buttons = (
            <OverlayTrigger placement="top" overlay={(<Tooltip id="tooltip">{colInfo.checkboxButton.text}</Tooltip>)} key={'checkboxButton'}>
                <span style={{ color: colInfo.checkboxButton.color }} onClick={() => onCheckboxButtonClicked(colInfo)}><i className={'fa ' + colInfo.checkboxButton.icon} style={{ margin: '10px 0' }} /></span>
            </OverlayTrigger>
        )
    }

    function filterClick(e: any) {
        hideShowFilter(colInfo, e, colIndex);
        setShowFilterMenu(!showFilterMenu);

        setTimeout(function () { document.getElementById("filter_" + colInfo.title)?.focus() }, 100);
    }

    let activeClassNameForFilter = ''
    let values = colUserSettings.selectedFilterValues
    if (values && ((colInfo.filterInfo?.filterType === 'CheckboxList' && values.length > 0) || (values.length === 1 && values[0] !== '')))
        activeClassNameForFilter = 'active'

    let resizeHandle = (
        <div
            style={{ height: resizeHandleHeight, gridColumn: 3 }}
            onMouseDown={() => onHoverGetIndex(colIndex)}
            onMouseOver={onHoverSetResizeHandleHeight}
            className={`resize-handle ${colResizeActiveIndex === colIndex ? 'active' : 'idle'}`}
        />
    )
    style.position = 'relative';

    if (isButtonColumn) {
        return (
            <th ref={colInfo.ref} className='button-column' style={{ width: style.width ? style.width : 50, position: 'relative' }}>
                {buttons}
                {colResize && resizeHandle}
            </th>
        );
    }
    else {
        return (
            <th ref={colInfo.ref} className={classnames} style={style}>
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 10px 12px' }}>
                    <div style={{ gridColumn: 1, overflow: 'hidden', whiteSpace: 'nowrap' }} onClick={sortClick}>
                        {title}
                        {sort && <span className={sortIcon}></span>}
                    </div>
                    {hasFilter &&
                        <div style={{ gridColumn: 2 }} className='filter'>
                            <span className={'filter-icon fa fa-filter ' + activeClassNameForFilter} onClick={filterClick} ></span>
                        </div>}
                    {colResize && resizeHandle}
                </div>
            </th>
        );
    }
}
export default TableHeaderCell;